import React, { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useHistory } from '@useHistory/useHistoryCompat';
import clsx from 'clsx';
import { Tooltip } from '@hevoio/hevo-ui-toolkit';
import { useKeyboardShortcutListeners } from '../../../a11y/shortcuts/hooks/useKeyboardShortcutListeners';
import { AppbarSupportBtnWrapper } from './appbar-support-cta/AppbarSupportBtnWrapper';
import styles from './Appbar.module.scss';
import { AppbarItem } from './appbar-item/AppbarItem';
import { SupportService } from '../../../support/interfaces/support';
import { KEYMAP } from '../../../a11y/shortcuts/constants/keyMap';
import { getDocLink, trackDocLink } from '../../../docs/utils/docLinkHandler';
import {
  RELEASE_NOTES_BASE_URL,
  TRACKER_DOCS_CTA_CLICK,
  TRACKER_VERSION_TAG_CLICK,
  TRACKER_VERSION_TAG_MOUSE_ENTER
} from '../../../core/constants/constants';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { ActivationStatusService } from '../../../core/services/activation-status.service';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import useService from '../../../injector/hooks/useService';
import { TeamService } from '../../../core/services/team.service';
import useTeamSettings from '../../../containers/team/hooks/useTeamSettingsService';
import { MultiRegionService } from '../../../auth/services/multi-region.service';
import { HEVO_DARK_THEME_LOGO_SVG } from '../../../shared/constants/brand-logo';
import { WorkspaceSwitcher } from './workspace-switcher/WorkspaceSwitcher';

declare const DASHBOARD_VERSION: any;

interface VersionInfo {
  version: string;
  releaseNotesLink: string;
}

interface AppbarProps {
  expanded?: boolean;
}

interface AppbarNavLinksProps {
  expanded: boolean;
  activatedDrawer: string | undefined;
  globalShortcutKeys: typeof KEYMAP.keyMaps.global.map;
  isJobsViewEnabled: boolean;
  isDpRegion: boolean;
  isTransformEnabled: boolean;
  isActivateAllowed: boolean;
  isActivateUser: boolean;
  isWorkFlowsEnabled: boolean;
  isLinkActive: (path: string) => boolean;
  onItemClick: (drawer?: string) => void;
}

function parseVersionInfo(): VersionInfo {
  const version = DASHBOARD_VERSION || '';
  const releaseVersionMatch = version.match(/v\d+\.\d+/);

  if (!releaseVersionMatch) {
    return { version, releaseNotesLink: '' };
  }

  // The release-notes link keeps the major.minor form (single-digit minors are
  // zero-padded to match the docs URL scheme); the tag displays the version
  // string as the build ships it.
  const releaseVersion = releaseVersionMatch.toString().replace(/v(\d+)\.(\d)$/, 'v$1.0$2');

  return {
    version,
    releaseNotesLink: `${RELEASE_NOTES_BASE_URL + releaseVersion}/`
  };
}

function AppbarNavLinks({
  expanded,
  activatedDrawer,
  globalShortcutKeys,
  isJobsViewEnabled,
  isDpRegion,
  isTransformEnabled,
  isActivateAllowed,
  isActivateUser,
  isWorkFlowsEnabled,
  isLinkActive,
  onItemClick
}: AppbarNavLinksProps) {
  return (
    <div className={styles.appbarLinks}>
      {isJobsViewEnabled ? (
        <AppbarItem
          navActive={isLinkActive('/all-jobs')}
          to='/all-jobs'
          activeDrawerLink={activatedDrawer}
          iconName='jobs'
          label='All Jobs'
          expanded={expanded}
          onClick={onItemClick}
        />
      ) : null}

      <AppbarItem
        navActive={isLinkActive('/pipeline/*')}
        to='/pipeline'
        activeDrawerLink={activatedDrawer}
        shortcut={globalShortcutKeys.pipelineList}
        iconName='pipeline'
        label='Pipelines'
        expanded={expanded}
        onClick={onItemClick}
      />

      {!isDpRegion ? (
        <AppbarItem
          navActive={isLinkActive('/model/*')}
          to='/model'
          activeDrawerLink={activatedDrawer}
          shortcut={globalShortcutKeys.modelList}
          iconName='models'
          label='Models'
          expanded={expanded}
          onClick={onItemClick}
        />
      ) : null}

      {isTransformEnabled && !isDpRegion ? (
        <AppbarItem
          navActive={isLinkActive('/transform/*')}
          to='/transform'
          activeDrawerLink={activatedDrawer}
          shortcut={globalShortcutKeys.transformList}
          iconName='transformation'
          label='Transformations'
          expanded={expanded}
          onClick={onItemClick}
          data-id='appbar-transform-tab'
        />
      ) : null}

      {isActivateAllowed && !isDpRegion ? (
        <AppbarItem
          styles={{ order: isActivateUser ? -1 : 2 }}
          navActive={isLinkActive('/activation/*') || isLinkActive('/target/*')}
          to='/activation'
          activeDrawerLink={activatedDrawer}
          shortcut={globalShortcutKeys.activateList}
          iconName='activate'
          label='Activate'
          expanded={expanded}
          onClick={onItemClick}
        />
      ) : null}

      {isWorkFlowsEnabled && !isDpRegion ? (
        <AppbarItem
          navActive={isLinkActive('/workflow/*')}
          to='/workflow'
          activeDrawerLink={activatedDrawer}
          shortcut={globalShortcutKeys.workflowList}
          iconName='workflow'
          label='Workflows'
          expanded={expanded}
          onClick={onItemClick}
        />
      ) : null}

      <AppbarItem
        navActive={isLinkActive('/destination/*')}
        to='/destination'
        activeDrawerLink={activatedDrawer}
        shortcut={globalShortcutKeys.warehouseList}
        iconName='destinations'
        label='Destinations'
        expanded={expanded}
        onClick={onItemClick}
      />
    </div>
  );
}

export function Appbar({ expanded = false }: AppbarProps) {
  const globalShortcutKeys = KEYMAP.keyMaps.global.map;

  const [supportChatHidden, setSupportChatHidden] = useState(false);
  const [isActivateAllowed, setIsActivateAllowed] = useState(false);
  const versionInfo = parseVersionInfo();
  const [activatedDrawer, setActivatedDrawer] = useState<string>();

  const analyticsTracker = useAnalyticsTracker();
  const { isWorkFlowsEnabled, getTeamSettings, isH2OEnabled, isPostLoadTransformationEnabled, isCerebraEnabled } =
    useTeamSettings();
  const history = useHistory();
  const location = useLocation();

  const supportService = useService(SupportService);
  const activateStatusService = useService(ActivationStatusService);
  const { teamDetails } = useService(TeamService);
  const multiRegionService = useService(MultiRegionService);
  const isDpRegion = multiRegionService.getSelectedRegionValue()?.type?.toLocaleLowerCase() === 'dp';

  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    setSupportChatHidden(supportService.isChatHiddenForTeam());
    setIsActivateAllowed(activateStatusService.shouldShowActivation());
    supportService.hideDefaultLauncher();
  }, []);

  // In a Cerebra deployment the legacy Zendesk chat is replaced by the widget, so close
  // the Zendesk messenger. Gate on the deployment build flag (isCerebraEnabled) — which is
  // known synchronously on mount — rather than the async per-user enable API, so a chat
  // left open doesn't restore and flash on the dashboard on reload while the API resolves.
  // Closing is non-destructive: the conversation is preserved and can be reopened via the
  // appbar support CTA (used by the rare build-on / API-off user).
  useEffect(() => {
    if (isCerebraEnabled) {
      supportService.hide();
    }
  }, [isCerebraEnabled]);

  useEffect(() => {
    const activeDrawer = searchParams.get('drawer');
    setActivatedDrawer(activeDrawer ?? undefined);
  }, [location]);

  const navigateTo = (path: string) => (event: KeyboardEvent) => {
    history.push(path);
    event.preventDefault();
  };

  useKeyboardShortcutListeners(
    {
      'global.team': navigateTo('/team'),
      'global.payment': navigateTo('/payment')
    },
    { priority: 0, terminal: 'match' }
  );

  const isLinkActive = (path: string) => !!matchPath(path, location.pathname);

  const onVersionTagMouseEnter = () => {
    analyticsTracker.eventTrack({ action: TRACKER_VERSION_TAG_MOUSE_ENTER });
  };

  const onDocsLinkClick = () => {
    trackDocLink('/', 'appbar');
    analyticsTracker.eventTrack({ action: TRACKER_DOCS_CTA_CLICK });
  };

  const onVersionTagClick = () => {
    analyticsTracker.eventTrack({ action: TRACKER_VERSION_TAG_CLICK });
  };

  const dataIdGenerator = getDataIdGenerator('appbar');
  const isJobsViewEnabled = !!getTeamSettings()?.show_jobs_view;
  const isActivateUser = teamDetails?.sign_up_source === 'ACTIVATE';
  const isTransformEnabled = isH2OEnabled() && isPostLoadTransformationEnabled();

  return (
    <div className={clsx(styles.appbar, expanded && styles.appbarExpanded)}>
      <div className={styles.appbarHeader}>
        <div className={clsx(styles.appbarBrandImageWrapper, expanded && styles.appbarBrandImageExpanded)}>
          <img className={styles.appbarBrandImage} src={HEVO_DARK_THEME_LOGO_SVG} alt='appbar-brand-img' />
        </div>
      </div>

      <div className={styles.appbarContent}>
        <WorkspaceSwitcher expanded={expanded} />
        <div className={clsx(styles.appbarDivider, expanded && styles.appbarDividerExpanded)} />

        <AppbarNavLinks
          expanded={expanded}
          activatedDrawer={activatedDrawer}
          globalShortcutKeys={globalShortcutKeys}
          isJobsViewEnabled={isJobsViewEnabled}
          isDpRegion={isDpRegion}
          isTransformEnabled={isTransformEnabled}
          isActivateAllowed={isActivateAllowed}
          isActivateUser={isActivateUser}
          isWorkFlowsEnabled={isWorkFlowsEnabled}
          isLinkActive={isLinkActive}
          onItemClick={setActivatedDrawer}
        />

        <div className={`${styles.appbarLinks} ${styles.appbarLinksBottom}`}>
          <AppbarItem
            to={getDocLink('/', 'appbar')}
            target='_blank'
            isExternalLink
            iconName='documentation'
            activeDrawerLink={activatedDrawer}
            label='Docs'
            expanded={expanded}
            onClick={onDocsLinkClick}
          />

          {!supportChatHidden ? (
            <div className={styles.supportBtnWrapper}>
              <AppbarSupportBtnWrapper expanded={expanded} />
            </div>
          ) : null}

          <div className={styles.appbarVersionTagWrapper}>
            <Tooltip dataId='version' title={versionInfo.version} placement='right'>
              <a
                data-id={dataIdGenerator('versionTag')}
                className={clsx(styles.appbarVersionTag)}
                onMouseEnter={onVersionTagMouseEnter}
                onClick={onVersionTagClick}
                target='_blank'
                href={getDocLink(versionInfo.releaseNotesLink || RELEASE_NOTES_BASE_URL, 'appbar')}
                rel='noreferrer'
              >
                {versionInfo.version}
              </a>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
